import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Props for the PrivateRoute component
 */
interface PrivateRouteProps {
  /** JSX element to be rendered if authentication passes */
  element: JSX.Element;
}

/**
 * Component that protects routes requiring authentication.
 * Redirects to login if user is not authenticated.
 * Shows loading spinner during authentication check.
 *
 * @param props - The component props
 * @returns The protected component or null/loading state
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const { isAuthenticated, loginWithRedirect, isLoading } = useAuth0();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !isRedirecting) {
      console.log("User not authenticated. Redirecting to login.");
      setIsRedirecting(true);
      const returnTo = `${window.location.pathname}${window.location.search}`;
      loginWithRedirect({
        appState: { returnTo },
      });
    }
  }, [isAuthenticated, isLoading, isRedirecting, loginWithRedirect]);

  if (isLoading || isRedirecting) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="size-32 animate-spin rounded-full border-y-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return element;
};

export default PrivateRoute;
